import * as amplitude from "@amplitude/analytics-browser";
import { isAHoliday } from "@redotech/redo-customer-portal-shared/utils/federal-holidays";
import { PickupLocation } from "@redotech/redo-model/return";
import {
  PickupPayer,
  pickupLocationOptions,
} from "@redotech/redo-model/return-flow";
import {
  RedoTimePicker,
  TimeOfDay,
} from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { FormCheckbox } from "@redotech/redo-web/checkbox";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { DateInput } from "@redotech/redo-web/date-picker";
import {
  LabelPosition,
  LabelTheme,
  LabeledInput,
} from "@redotech/redo-web/labeled-input";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { InputLines, TextInput } from "@redotech/redo-web/text-input";
import {
  InputSchema,
  groupInput,
  input,
  optionalInput,
} from "@redotech/ui/form";
import { optionalEqual } from "@redotech/util/equal";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import classNames from "classnames";
import { memo, useContext, useEffect, useMemo } from "react";
import { PickupContext } from "../../../contexts/PickupContext";
import * as review from "../review.module.css";

const pickupDetailsForm = groupInput(
  {
    location: input<PickupLocation | undefined>({
      validator: (location) => (location ? [] : ["Required"]),
    }),
    date: input<Temporal.PlainDate | undefined>({
      equal: optionalEqual((a, b) => a.equals(b)),
      validator: (date) => (date !== undefined ? [] : ["Required"]),
    }),
    specialInstructions: input<string>(),
    textReminder: input<boolean>(),
    pickupPayer: input<PickupPayer | undefined>(),
    pickupReadyTime: input<TimeOfDay | undefined>(),
  },
  {
    validator: ({ location, specialInstructions }) =>
      location !== PickupLocation.OTHER || specialInstructions
        ? []
        : ["Instructions required"],
  },
);

type PickupDetails = InputSchema.Value<typeof pickupDetailsForm>;

export const pickupForm = groupInput({
  finalized: input<boolean>({
    validator: (value) => (value ? [] : ["Required"]),
  }),
  pickup: optionalInput(
    pickupDetailsForm,
    (): PickupDetails => ({
      location: undefined,
      date: undefined,
      specialInstructions: "",
      textReminder: false,
      pickupPayer: undefined,
      pickupReadyTime: undefined,
    }),
  ),
});

export type PickupForm = InputSchema.Input<typeof pickupForm>;

const getValidDate = (date: Temporal.PlainDate, days: number) => {
  date = date.add({ days });

  while (!isValidDate(date)) {
    date = date.add({ days: 1 });
  }

  return date;
};

// FedEx does not allow pickups on weekends or holidays
const isValidDate = (date: Temporal.PlainDate) => {
  return (
    date.dayOfWeek !== 7 && date.dayOfWeek !== 6 && !isFederalHoliday(date)
  );
};

const isFederalHoliday = (date: Temporal.PlainDate) => {
  // Convert Temporal.PlainDate to JavaScript Date
  const jsDate = new Date(date.year, date.month - 1, date.day);
  return isAHoliday(jsDate);
};

const textReminderEligible = (pickupDate: Temporal.PlainDate | undefined) => {
  // We only want to allow users to opt into reminder texts if the pickup is at least 2 days away
  if (!pickupDate) {
    return false;
  }

  const today = Temporal.Now.plainDateISO();
  return today.until(pickupDate).days > 1;
};

export const PickupFormCard = memo(function PickupFormCard({
  upsell,
  labelDeducted,
  setLoading,
  isReviewStep,
  isMerchantDashboard,
  isStatusPage,
  input,
  pending,
  setPending,
  errorMessage,
  showPayer,
  showPickupReadyTimeSelector,
}: {
  upsell?: number | undefined;
  labelDeducted?: boolean;
  setLoading?: (loading?: boolean) => void;
  isReviewStep?: boolean;
  isMerchantDashboard?: boolean;
  isStatusPage?: boolean;
  input?: PickupForm;
  pending?: boolean;
  setPending?: (pending: boolean) => void;
  errorMessage?: string;
  showPayer?: boolean;
  showPickupReadyTimeSelector?: boolean;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const today = useMemo(() => Temporal.Now.plainDateISO(), []);

  const minDate = getValidDate(today, 1);
  // FedEx can be scheduled up to 14 days in advance
  const maxDate = getValidDate(today, 14);

  const newInput = useContext(PickupContext).input;
  input = input ?? newInput;

  const { finalized, pickup } = input.inputs;
  const {
    date,
    location,
    specialInstructions,
    textReminder,
    pickupPayer,
    pickupReadyTime,
  } = pickup.input?.inputs ?? {};

  useEffect(() => {
    if (pickup.value?.date === undefined) {
      // @ts-expect-error: strict-null-checks
      date.setValue(minDate);
    }

    if (pickup.value?.location === undefined) {
      // @ts-expect-error: strict-null-checks
      location.setValue(PickupLocation.FRONT_DOOR);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={review.pickupForm}>
      {!isMerchantDashboard && (
        <div className={review.summaryCardItem}>
          <div className={review.summaryText}>
            Your package will be picked up from the pickup address on{" "}
            {isReviewStep ? "the next page." : "this page."}{" "}
            {upsell &&
              (labelDeducted ? (
                <>
                  An additional charge of{" "}
                  <span className={review.price}>{formatCurrency(upsell)}</span>{" "}
                  will be deducted from your refund.
                </>
              ) : (
                <>
                  You will be charged an additional charge of{" "}
                  <span className={review.price}>{formatCurrency(upsell)}</span>
                  .
                </>
              ))}
          </div>
        </div>
      )}
      <div className={classNames(review.summaryCardItem, review.center)}>
        <LabeledInput
          label="Pickup date"
          position={LabelPosition.ABOVE}
          theme={LabelTheme.THIN_BOLD}
        >
          <DateInput
            // @ts-expect-error: strict-null-checks
            error={date.allErrors.length > 0}
            filterDate={({ date }) => {
              return isValidDate(dateToCurrentPlainDate(date));
            }}
            includeYearNav={false}
            maxDate={plainDateToCurrentDate(maxDate)}
            minDate={plainDateToCurrentDate(minDate)}
            minDetail="year"
            // @ts-expect-error: strict-null-checks
            value={date.value ? plainDateToCurrentDate(date.value) : undefined}
            valueChange={(value) => {
              if (value && isValidDate(dateToCurrentPlainDate(value))) {
                // @ts-expect-error: strict-null-checks
                date.setValue(dateToCurrentPlainDate(value));
              } else {
                // @ts-expect-error: strict-null-checks
                date.setValue(undefined);
              }
            }}
          />
        </LabeledInput>
      </div>
      {/* @ts-expect-error: strict-null-checks */}
      {date.allErrors.length > 0 && (
        <div className={classNames(review.summaryCardItem, review.left)}>
          {/* @ts-expect-error: strict-null-checks */}
          <div className={review.error}>{date.allErrors[0]}</div>
        </div>
      )}
      {showPickupReadyTimeSelector && (
        <LabeledInput
          description="Leave blank if you'd like to use the default pickup ready time for the customer's local service center."
          label="Pickup ready time"
          position={LabelPosition.ABOVE}
          theme={LabelTheme.THIN_BOLD}
        >
          <RedoTimePicker
            setValue={(value: TimeOfDay | null) =>
              // @ts-expect-error: strict-null-checks
              pickupReadyTime.setValue(value ?? undefined)
            }
            // @ts-expect-error: strict-null-checks
            value={pickupReadyTime.value ?? null}
          />
        </LabeledInput>
      )}
      <LabeledInput
        description={
          <div className={review.summaryCardItem}>
            <div className={review.pickupInputDescription}>
              If {isMerchantDashboard ? "the customer lives" : "you live"} in a
              multi-story building, select an option which is ground level and
              secure.
            </div>
          </div>
        }
        label="Pickup location"
        position={LabelPosition.ABOVE}
        theme={LabelTheme.THIN_BOLD}
      >
        <SelectDropdown
          className={review.pickupLocationDropdown}
          options={[...pickupLocationOptions().keys()]}
          // @ts-expect-error: strict-null-checks
          value={location.value}
          // @ts-expect-error: strict-null-checks
          valueChange={(value) => location.setValue(value)}
        >
          {(option) => pickupLocationOptions().get(option)?.label}
        </SelectDropdown>
      </LabeledInput>
      {/* @ts-expect-error: strict-null-checks */}
      {location.value === PickupLocation.OTHER && (
        <TextInput
          fullwidth
          lines={InputLines.MULTI}
          maxLength={200}
          // @ts-expect-error: strict-null-checks
          onChange={(val) => specialInstructions.setValue(val)}
          placeholder="Special instructions"
          // @ts-expect-error: strict-null-checks
          value={specialInstructions.value}
        />
      )}
      {errorMessage && (
        <div className={review.summaryCardItem}>
          <div className={review.error}>{errorMessage}</div>
        </div>
      )}
      {isMerchantDashboard && showPayer ? (
        <LabeledInput
          description={
            <div className={review.summaryCardItem}>
              <div className={review.pickupInputDescription}>
                This is who will pay for the pickup fee.
              </div>
            </div>
          }
          label="Payer"
          position={LabelPosition.ABOVE}
          theme={LabelTheme.THIN_BOLD}
        >
          <SelectDropdown
            className={review.pickupLocationDropdown}
            options={[PickupPayer.CUSTOMER_DEDUCT, PickupPayer.MERCHANT]}
            // @ts-expect-error: strict-null-checks
            value={pickupPayer.value}
            // @ts-expect-error: strict-null-checks
            valueChange={(value) => pickupPayer.setValue(value)}
          >
            {(option) => {
              switch (option) {
                case PickupPayer.CUSTOMER_DEDUCT:
                  return "Customer";

                case PickupPayer.MERCHANT:
                  return "Merchant";
              }
              return undefined;
            }}
          </SelectDropdown>
        </LabeledInput>
      ) : null}

      {/* @ts-expect-error: strict-null-checks */}
      {!isMerchantDashboard && textReminderEligible(date.value) && (
        <div className={review.reminderTextItem}>
          {/* @ts-expect-error: strict-null-checks */}
          <FormCheckbox input={textReminder} />
          <span>Send me text reminders and updates about my pickup.</span>
        </div>
      )}
      {!isMerchantDashboard && (
        <Button
          disabled={!!pickup.allErrors.length}
          onClick={() => {
            if (!isMerchantDashboard && !isStatusPage) {
              amplitude.logEvent("pickup-saved", {
                // @ts-expect-error: strict-null-checks
                date: date.toString(),
                // @ts-expect-error: strict-null-checks
                location: location.value,
                // @ts-expect-error: strict-null-checks
                specialInstructions: specialInstructions.value,
                textReminder: pickup.value?.textReminder,
              });
            }
            if (setLoading) {
              setLoading(true);
            }
            if (setPending) {
              setPending(true);
            }
            finalized.setValue(true);
          }}
          pending={pending}
          theme={ButtonTheme.BRAND}
        >
          {`${isStatusPage ? "Reschedule" : "Add"} package pickup`}
        </Button>
      )}
    </div>
  );
});
